import { RootState } from '@/store';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  App,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Image,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updateField } from '../../contextRedux/InterventionFileProject/InterventionFileProjectModel';
import InterventionFileProjectService from '../../services/interventionFileProject/InterventionFileProjectService';
import PmContractService from '../../services/pmContract/PmContractService';

const { TabPane } = Tabs;
const { Text, Title } = Typography;

interface DeviceInstallation {
  deviceTypeId: number;
  deviceName: string;
  total: number;
  installed: number;
  overInstalled: number;
  technicians: {
    id: number;
    name: string;
    quantity: number;
  }[];
}

// Add interface for contact person
interface ContactPerson {
  name: string;
  role: string;
  phone: string;
}

const InterventionFileProject: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { modal, message } = App.useApp();
  const [addForm] = Form.useForm();
  const [editingInstallationIndex, setEditingInstallationIndex] = useState<number | null>(null);
  const [editingInstallation, setEditingInstallation] = useState<any>(null);
  const [deviceInstallations, setDeviceInstallations] = useState<DeviceInstallation[]>([]);
  const [selectedWorkplace, setSelectedWorkplace] = useState<string | null>(null);
  const [filteredTechnicians, setFilteredTechnicians] = useState<any[]>([]);
  const [installations, setInstallations] = useState<any[]>([]);

  const [_isWorkPlaceSelectDisabled, setIsWorkPlaceSelectDisabled] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const paramData = useMemo(() => {
    const data = searchParams.get('data');
    return data ? JSON.parse(data) : null;
  }, [searchParams]);

  // Get parameters from URL
  const editId = searchParams.get('id');
  const pmContractId = searchParams.get('pmContractId') || paramData?.pmContractId;

  const interventionProjectFile = useSelector((state: RootState) => state.models.interventionProjectFile);
  const installedDevicesWithQuantity = interventionProjectFile.installedDevicesWithQuantity;
  const pmContract = interventionProjectFile.pmContract;
  const addedDevices = pmContract?.pmContractWorkPlaceItems ?? [];

  const { data: _pmContract, isPending: contractLoading } = useQuery({
    queryKey: ['pmContractProjects', pmContractId],
    queryFn: () =>
      PmContractService.search({
        ids: [pmContractId],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
      }).then((data) => {
        dispatch(updateField({ field: 'pmContract', value: data?.response[0] }));
        dispatch(updateField({ field: 'pmContractId', value: data?.response[0]?.id }));
        dispatch(updateField({ field: 'company', value: data?.response[0]?.company }));
        dispatch(updateField({ field: 'companyId', value: data?.response[0]?.companyId }));
        return data?.response[0];
      }),
    enabled: !!pmContractId, // Only fetch PM contract if not editing
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  // Memoize devicesByWorkplace to prevent unnecessary recalculations
  const devicesByWorkplace = useMemo(() => {
    return addedDevices.reduce((acc: any, device: any) => {
      const workplaceName = device.workPlace?.name || 'Unknown';
      if (!acc[workplaceName]) {
        acc[workplaceName] = [];
      }
      acc[workplaceName].push(device);
      return acc;
    }, {});
  }, [addedDevices]);

  // Image upload validation
  const uploadImage = async (file: any) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await InterventionFileProjectService.upload(formData);
      const data = await response.json();
      form.setFieldsValue({
        ...form.getFieldsValue(true),
        attachedImage: data?.response,
      });
      dispatch(updateField({ field: 'attachedImage', value: data?.response ?? null }));

      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
      } else {
        message.success('Imagine incarcata!');
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleDeleteImage = () => {
    // Reset the image in the form
    form.setFieldsValue({
      ...form.getFieldsValue(true),
      attachedImage: '',
    });

    // Update the Redux state to remove the image
    dispatch(updateField({ field: 'attachedImage', value: '' }));

    message.success('Imagine stearsa!');
  };

  const beforeUpload = (file: any) => {
    const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isImage) {
      message.error('Poti adauga doar imagini (JPEG sau PNG)!');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const getUploadProps = () => ({
    listType: 'picture' as 'picture',
    maxCount: 1,
    beforeUpload: (file: any) => beforeUpload(file),
    customRequest: ({ file, onSuccess, onError }: any) => {
      uploadImage(file)
        .then(() => {
          onSuccess('ok');
        })
        .catch((error) => {
          onError(error);
        });
    },
  });

  const handleSubmit = async () => {
    try {
      // Validate form fields
      await form.validateFields();

      const submissionData: any = {
        pmContractId: interventionProjectFile.pmContractId,
        companyId: interventionProjectFile.companyId,
        workPlaceId: interventionProjectFile.workPlaceId,
        attachedImage: interventionProjectFile.attachedImage,
        observations: interventionProjectFile.observations,
        hours: interventionProjectFile.hours,
        minutes: interventionProjectFile.minutes,
        traveledKilometers: interventionProjectFile.traveledKilometers,
        timeTransportHours: interventionProjectFile.timeTransportHours,
        timeTransportMinutes: interventionProjectFile.timeTransportMinutes,
        deliveryDate: interventionProjectFile.deliveryDate,
        interventionFileProjectInstallations: installations.map((installation) => ({
          id: installation.id || '',
          interventionProjectFileId: interventionProjectFile.id || '',
          deviceTypeId: installation.deviceTypeId,
          stage: installation?.deviceType?.stage ?? installation?.stage,
          quantity: installation.quantity,
          interventionFileProjectInstallationTehnicians: installation.technicians.map((tech: any) => ({
            id: '',
            userId: tech.id,
            interventionFileProjectInstallationId: '',
          })),
        })),
      };

      const confirmed = await new Promise((resolve) => {
        modal.confirm({
          title: 'Confirmare date',
          content: (
            <div>
              <p>Workplace: {selectedWorkplace}</p>
              <p>Total instalări: {installations.length}</p>
              <p>Detalii instalări:</p>
              {deviceInstallations.map((device) => (
                <div key={device.deviceTypeId}>
                  <p>{device.deviceName}:</p>
                  <ul>
                    {device.technicians
                      .filter((tech) => tech.quantity > 0)
                      .map((tech) => (
                        <li key={tech.id}>
                          {tech.name}: {tech.quantity} buc.
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!confirmed) return;

      const response: any = await InterventionFileProjectService.upsert(editId || null, submissionData);
      if (response.errors && response.errors.length > 0) {
        response.errors.forEach((error: string) => message.error(error));
      } else {
        const action = editId ? 'modificată' : 'salvată';
        message.success(`Fișa de intervenție a fost ${action}`);

        // Navigate to PM contract project edit page after saving
        if (response?.response?.id) {
          setTimeout(() => {
            navigate(`/project-management/pmContractProject/edit?id=${interventionProjectFile.pmContractId}`);
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error submitting intervention file:', error);
      message.error('Failed to update installation progress');
    }
  };

  // Update the workplace selection handler
  const handleWorkplaceChange = async (value: any) => {
    if (!value) {
      form.resetFields();
      setInstallations([]);
      setSelectedWorkplace(null);
      setFilteredTechnicians([]);
      setDeviceInstallations([]);
      return;
    }

    form.resetFields();
    setInstallations([]);
    setSelectedWorkplace(value);
    const selectedWorkPlace = devicesByWorkplace[value][0]?.workPlace;

    // Set the form value for workplace
    form.setFieldsValue({ workPlace: value });

    dispatch(updateField({ field: 'workPlace', value: selectedWorkPlace }));
    dispatch(updateField({ field: 'workPlaceId', value: selectedWorkPlace.id }));

    // Filter devices for the selected workplace
    const devices = addedDevices.filter((device: any) => device.workPlace?.name === value);

    // Filter and deduplicate technicians for the selected workplace
    const workplaceTechnicians = devices.flatMap((device: any) =>
      device.pmContractWorkPlaceItemTehnicians.map((pm: any) => pm?.user)
    );
    const uniqueTechnicians = Array.from(new Set(workplaceTechnicians.map((tech: any) => tech?.id))).map((id) =>
      workplaceTechnicians.find((tech: any) => tech?.id === id)
    );

    setFilteredTechnicians(uniqueTechnicians);

    // Initialize device installations for this workplace
    setDeviceInstallations(
      devices.map((device: any) => ({
        deviceTypeId: device.id,
        deviceName: device.device.name,
        total: device.piece,
        installed: 0,
        overInstalled: 0,
        technicians: [],
      }))
    );
    setIsWorkPlaceSelectDisabled(true);
    await getQuantityOfInstalledDevice(pmContract.id, selectedWorkPlace.id);
  };

  const getQuantityOfInstalledDevice = async (pmContractId: any, workPlaceId: any) => {
    const result: any = await InterventionFileProjectService.getQuantityOfInstalledDevice({
      interventionFileProjectInstallations: true,
      pmContractId: pmContractId,
      workPlaceId: workPlaceId,
    });

    if (result?.errors?.length == 0) {
      dispatch(updateField({ field: 'installedDevicesWithQuantity', value: result?.response }));
    }
  };

  // Helper function to get unique contact persons for a workplace
  const getWorkplaceContacts = (workplaceName: string): ContactPerson[] => {
    const devices = addedDevices.filter((device: any) => device.workPlace?.name === workplaceName);
    const contactsMap = new Map();

    devices.forEach((device: any) => {
      if (device.agent) {
        const key = device.agent.id;
        contactsMap.set(key, {
          name: device.agent.name,
          role: device.agent.role[0],
          phone: device.agent.phone[0],
        });
      }
    });

    return Array.from(contactsMap.values());
  };

  // Add or update installation
  const handleInstallation = (e: React.FormEvent) => {
    e.preventDefault();
    addForm
      .validateFields()
      .then((values) => {
        const device = devicesByWorkplace[selectedWorkplace!].find((d: any) => d.id === values.deviceTypeId);

        const technicians = filteredTechnicians
          .filter((t) => values.technicianIds.includes(t.id))
          .map((t) => ({ id: t.id, name: t.name }));

        const newInstallation = {
          id: editingInstallation?.id || '',
          deviceTypeId: device.device.id,
          deviceName: device.device.name,
          stage: device.pmContractWorkPlaceItemStage?.name || '-',
          technicians,
          quantity: values.quantity,
        };

        if (editingInstallationIndex !== null) {
          setInstallations((prev) =>
            prev.map((item, index) => (index === editingInstallationIndex ? newInstallation : item))
          );
        } else {
          setInstallations((prev) => [...prev, newInstallation]);
        }

        // Reset form and editing state
        setEditingInstallation(null);
        setEditingInstallationIndex(null);
        addForm.resetFields();
      })
      .catch((error) => {
        console.error('Validation failed:', error);
      });
  };

  // Start editing an installation
  const startEditing = (index: number) => {
    setEditingInstallationIndex(index);
    const record = installations[index];
    setEditingInstallation(record);

    // Find the device in devicesByWorkplace that matches the record
    const device = devicesByWorkplace[selectedWorkplace!].find((d: any) => d.device.id === record.deviceTypeId);

    // Set all form fields with correct values
    addForm.setFieldsValue({
      deviceTypeId: device?.id,
      etapa: record.stage,
      technicianIds: record.technicians.map((t: any) => t.id),
      quantity: record.quantity,
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingInstallation(null);
    addForm.resetFields();
  };

  // Delete installation
  const deleteInstallation = (index: number) => {
    setInstallations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTechnicianSelect = (value: string[]) => {
    if (value.includes('all')) {
      addForm.setFieldsValue({
        technicianIds: filteredTechnicians?.map((tech: any) => tech?.id),
      });
    }
  };

  // First, add these breakpoint-related styles at the top of your component
  const getResponsiveColumns = () => {
    const screens = {
      xs: { span: 24 }, // phones
      sm: { span: 12 }, // tablets
      md: { span: 8 }, // small laptop
    };
    return screens;
  };

  useEffect(() => {
    if (addedDevices.length > 0) {
      setDeviceInstallations(
        addedDevices.map((device: any) => ({
          deviceTypeId: device.id,
          deviceName: device.device.name,
          total: device.piece,
          installed: 0,
          overInstalled: 0,
          technicians: [],
        }))
      );
    }
  }, [addedDevices]);

  // Fetch existing intervention file when editing
  useEffect(() => {
    if (editId) {
      //setProjectLoading(true);
      InterventionFileProjectService.search({
        ids: [editId],
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isInterventionFilePerformOperationsIncluded: true,
        isInterventionFileEquipmentsIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        isRequestFileIncluded: true,
      }).then((data: any) => {
        const interventionFileProject = data?.response?.[0];
        if (interventionFileProject) {
          // Update Redux state with the fetched data
          dispatch(updateField({ field: 'id', value: interventionFileProject.id }));
          dispatch(updateField({ field: 'pmContractId', value: interventionFileProject.pmContractId }));
          dispatch(updateField({ field: 'companyId', value: interventionFileProject.companyId }));
          dispatch(updateField({ field: 'workPlaceId', value: interventionFileProject.workPlaceId }));
          dispatch(updateField({ field: 'workPlace', value: interventionFileProject.workPlace }));
          dispatch(updateField({ field: 'company', value: interventionFileProject.company }));
          dispatch(updateField({ field: 'hours', value: interventionFileProject.hours }));
          dispatch(updateField({ field: 'minutes', value: interventionFileProject.minutes }));
          dispatch(updateField({ field: 'traveledKilometers', value: interventionFileProject.traveledKilometers }));
          dispatch(updateField({ field: 'timeTransportHours', value: interventionFileProject.timeTransportHours }));
          dispatch(updateField({ field: 'timeTransportMinutes', value: interventionFileProject.timeTransportMinutes }));
          dispatch(updateField({ field: 'attachedImage', value: interventionFileProject.attachedImage }));
          dispatch(updateField({ field: 'observations', value: interventionFileProject.observations }));
          dispatch(updateField({ field: 'deliveryDate', value: interventionFileProject.deliveryDate }));
          dispatch(
            updateField({
              field: 'interventionFileProjectInstallations',
              value: interventionFileProject.interventionFileProjectInstallations,
            })
          );

          // Map installations for the form
          const mappedInstallations = (interventionFileProject.interventionFileProjectInstallations || []).map(
            (installation: any) => ({
              id: installation.id,
              deviceTypeId: installation.deviceTypeId,
              deviceName: installation.deviceType?.name || '',
              stage: installation.deviceType?.stage || installation?.stage,
              technicians: (installation.interventionFileProjectInstallationTehnicians || []).map((tech: any) => ({
                id: tech.userId,
                name: tech.user?.name || '',
              })),
              quantity: installation.quantity,
            })
          );
          setInstallations(mappedInstallations);

          // Set form fields
          form.setFieldsValue({
            workPlace: interventionFileProject.workPlace?.name,
            deliveryDate: interventionFileProject.deliveryDate,
            timeTransportHours: interventionFileProject.timeTransportHours,
            timeTransportMinutes: interventionFileProject.timeTransportMinutes,
            traveledKilometers: interventionFileProject.traveledKilometers,
            hours: interventionFileProject.hours,
            minutes: interventionFileProject.minutes,
            observations: interventionFileProject.observations,
            attachedImage: interventionFileProject.attachedImage,
          });

          // Set selected workplace for UI
          if (interventionFileProject.workPlace?.name) {
            setSelectedWorkplace(interventionFileProject.workPlace.name);
          }
        }
        return interventionFileProject;
      });
    }
  }, [editId, dispatch]);

  // useEffect(() => {
  //   handleWorkplaceChange(interventionProjectFile?.workPlace?.name);
  // }, [addedDevices.length > 0]);

  if (contractLoading) {
    return (
      <div className='loading-container'>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div className='intervention-file-container'>
      <Tabs defaultActiveKey='1' className='modern-tabs'>
        <TabPane tab='Raport Zilnic' key='2'>
          <Card className='modern-card'>
            <Form form={form} layout='vertical'>
              <Form.Item
                name='workPlace'
                label='Punct de lucru'
                className='workplace-select'
                rules={[{ required: true, message: 'Vă rugăm să selectați un punct de lucru!' }]}
              >
                <Select
                  onChange={handleWorkplaceChange}
                  placeholder='Selectați punctul de lucru'
                  className='modern-select'
                  //disabled={isWorkPlaceSelectDisabled}
                >
                  {Object.keys(devicesByWorkplace).map((workplace) => (
                    <Select.Option key={workplace} value={workplace}>
                      {workplace}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedWorkplace && (
                <>
                  <div className='contact-card'>
                    <Title level={5}>Persoane de contact</Title>
                    <div className='contact-grid'>
                      {getWorkplaceContacts(selectedWorkplace).map((contact, index) => (
                        <div key={index} className='contact-item'>
                          <Space direction='vertical' size='small'>
                            <Text strong>{contact.name}</Text>
                            <Text type='secondary'>{contact.role}</Text>
                            <Text>{contact.phone}</Text>
                          </Space>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Card className='time-input-card' style={{ marginBottom: 16 }} title='Durata Intervenției'>
                    <Form layout='vertical'>
                      <Row gutter={[16, 16]} style={{ alignItems: 'center' }}>
                        <Col {...getResponsiveColumns()}>
                          <Form.Item
                            label='Data Intervenției'
                            name='deliveryDate'
                            rules={[{ required: true, message: 'Alegeti o data!' }]}
                          >
                            <Input
                              type='date'
                              value={interventionProjectFile.deliveryDate}
                              onChange={(e: any) =>
                                dispatch(updateField({ field: 'deliveryDate', value: e.target.value }))
                              }
                            />
                          </Form.Item>
                        </Col>

                        <Col {...getResponsiveColumns()}>
                          <Form.Item
                            label='Timp transport (ore)'
                            name='timeTransportHours'
                            rules={[{ required: true, message: 'Introduceti timpul de transport in ore!' }]}
                          >
                            <InputNumber
                              placeholder='2,6'
                              style={{ width: '100%' }}
                              value={interventionProjectFile.timeTransportHours}
                              onChange={(e: any) => dispatch(updateField({ field: 'timeTransportHours', value: e }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col {...getResponsiveColumns()}>
                          <Form.Item
                            label='Timp transport (minute)'
                            name='timeTransportMinutes'
                            rules={[{ required: true, message: 'Introduceti timpul de transport in minute!' }]}
                          >
                            <InputNumber
                              placeholder='2,6'
                              style={{ width: '100%' }}
                              value={interventionProjectFile.timeTransportMinutes}
                              onChange={(e: any) => dispatch(updateField({ field: 'timeTransportMinutes', value: e }))}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} style={{ alignItems: 'center' }}>
                        <Col {...getResponsiveColumns()}>
                          <Form.Item
                            label='Kilometri parcursi'
                            name='traveledKilometers'
                            rules={[{ required: true, message: 'Introduceti numarul de kilometri!' }]}
                          >
                            <InputNumber
                              placeholder='Kilometri'
                              style={{ width: '100%' }}
                              value={interventionProjectFile.traveledKilometers}
                              onChange={(e: any) => dispatch(updateField({ field: 'traveledKilometers', value: e }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col {...getResponsiveColumns()}>
                          <Form.Item
                            name='hours'
                            label='Durata in ore interventie'
                            rules={[{ required: true, message: 'Introduceți orele' }]}
                          >
                            <InputNumber
                              min={0}
                              style={{ width: '100%' }}
                              placeholder='Ore'
                              value={interventionProjectFile.hours}
                              onChange={(e: any) => dispatch(updateField({ field: 'hours', value: e }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col {...getResponsiveColumns()}>
                          <Form.Item
                            name='minutes'
                            label='Durata in minute interventie'
                            rules={[
                              { required: true, message: 'Introduceți minutele' },
                              { type: 'number', max: 59, message: 'Minutele trebuie să fie între 0 și 59' },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              max={59}
                              style={{ width: '100%' }}
                              placeholder='Minute'
                              value={interventionProjectFile.minutes}
                              onChange={(e: any) => dispatch(updateField({ field: 'minutes', value: e }))}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </Card>

                  {/* Add new installation form */}
                  <Card
                    className='installation-form-card'
                    style={{ marginBottom: 16 }}
                    title={editingInstallation ? 'Editare Instalare' : 'Adăugare Instalare Nouă'}
                    extra={
                      editingInstallation && (
                        <Button onClick={cancelEditing} icon={<CloseOutlined />}>
                          Anulează
                        </Button>
                      )
                    }
                  >
                    <Form form={addForm} layout='vertical' onFinish={handleInstallation}>
                      <Row gutter={[16, 16]}>
                        <Col {...getResponsiveColumns()} style={{ minWidth: 200 }}>
                          <Form.Item
                            name='deviceTypeId'
                            label='Dispozitiv'
                            rules={[{ required: true, message: 'Selectați dispozitivul' }]}
                          >
                            <Select
                              placeholder='Selectează dispozitiv'
                              showSearch
                              optionFilterProp='children'
                              onChange={(value) => {
                                const selectedDevice = devicesByWorkplace[selectedWorkplace].find(
                                  (device: any) => device.id === value
                                );
                                addForm.setFieldsValue({
                                  etapa: selectedDevice?.pmContractWorkPlaceItemStage?.name || '',
                                });
                              }}
                            >
                              {devicesByWorkplace[selectedWorkplace].map((device: any) => (
                                <Select.Option key={device.id} value={device.id}>
                                  {device.device.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col {...getResponsiveColumns()} style={{ minWidth: 200 }}>
                          <Form.Item
                            name='etapa'
                            label='Etapa'
                            rules={[{ required: true, message: 'Introduceți etapa' }]}
                          >
                            <Input placeholder='Etapa' disabled />
                          </Form.Item>
                        </Col>
                        <Col {...getResponsiveColumns()} style={{ minWidth: 200 }}>
                          <Form.Item
                            name='technicianIds'
                            label='Tehnicieni'
                            rules={[{ required: true, message: 'Selectați cel puțin un tehnician' }]}
                          >
                            <Select
                              mode='multiple'
                              placeholder='Selectează tehnicieni'
                              onChange={handleTechnicianSelect}
                              maxTagCount={2}
                              maxTagTextLength={12}
                            >
                              <Select.Option key='all' value='all'>
                                Selectează toți tehnicienii
                              </Select.Option>
                              {filteredTechnicians.map((tech: any) => (
                                <Select.Option key={tech?.id} value={tech?.id}>
                                  {tech?.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col {...getResponsiveColumns()} style={{ minWidth: 200 }}>
                          <Form.Item
                            name='quantity'
                            label='Cantitate'
                            rules={[{ required: true, message: 'Introduceți cantitatea' }]}
                          >
                            <InputNumber min={1} style={{ width: '100%' }} placeholder='Cantitate' />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={24}
                          lg={24}
                          xl={24}
                          style={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          <Button
                            type='primary'
                            htmlType='button'
                            icon={editingInstallation ? <CheckOutlined /> : <PlusOutlined />}
                            onClick={handleInstallation}
                          >
                            {editingInstallation ? 'Salvează' : 'Adaugă'}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </Card>

                  {/* Display installations table */}
                  <div style={{ overflowX: 'auto' }}>
                    <Table
                      className='installations-table'
                      columns={[
                        {
                          title: 'Dispozitiv',
                          dataIndex: 'deviceName',
                          width: '25%',
                          ellipsis: true,
                        },
                        {
                          title: 'Etapa',
                          dataIndex: 'stage',
                          width: '15%',
                          ellipsis: true,
                        },
                        {
                          title: 'Tehnicieni',
                          dataIndex: 'technicians',
                          width: '30%',
                          ellipsis: true,
                          render: (technicians) => (
                            <Space wrap>
                              {technicians.map((tech: any) => (
                                <Tag key={tech.id}>{tech.name}</Tag>
                              ))}
                            </Space>
                          ),
                        },
                        {
                          title: 'Cantitate',
                          dataIndex: 'quantity',
                          width: '20%',
                          render: (quantity) => <Tag color='blue'>{quantity}</Tag>,
                        },
                        {
                          title: 'Acțiuni',
                          width: '10%',
                          render: (_, _record, index) => (
                            <Space>
                              <Button type='link' icon={<EditOutlined />} onClick={() => startEditing(index)} />
                              <Button
                                type='link'
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => deleteInstallation(index)}
                              />
                            </Space>
                          ),
                        },
                      ]}
                      dataSource={installations}
                      pagination={false}
                      scroll={{ x: 800 }} // Enable horizontal scrolling on small screens
                    />
                  </div>

                  {/* Summary table with responsive design */}
                  <div style={{ overflowX: 'auto', marginTop: '16px' }}>
                    <Table
                      className='summary-table'
                      columns={[
                        {
                          title: 'Dispozitiv',
                          dataIndex: 'deviceName',
                          width: '25%',
                          ellipsis: true,
                        },
                        {
                          title: 'Etapa',
                          dataIndex: 'stage',
                          width: '15%',
                          ellipsis: true,
                        },
                        {
                          title: 'Total de instalat',
                          dataIndex: 'total',
                          width: '30%',
                          render: (total) => <Tag color='processing'>{total}</Tag>,
                        },
                        {
                          title: 'Total instalat',
                          width: '30%',
                          render: (_, record: any) => {
                            const installed = installations
                              .filter((i: any) => i.deviceTypeId === record.deviceTypeId)
                              .reduce((sum: any, i: any) => sum + i.quantity, 0);
                            const overInstalled = Math.max(0, installed - record.total);

                            return (
                              <Badge
                                count={overInstalled > 0 ? `+${overInstalled}` : 0}
                                style={{
                                  backgroundColor: overInstalled > 0 ? '#ff4d4f' : '#52c41a',
                                }}
                              >
                                <Tag color={installed > record.total ? 'error' : 'success'}>{installed}</Tag>
                              </Badge>
                            );
                          },
                        },
                      ]}
                      dataSource={(devicesByWorkplace[selectedWorkplace] || []).map((addedDevice: any) => {
                        const matchedDevice = installedDevicesWithQuantity.find(
                          (idwq: any) => idwq?.deviceTypeId === addedDevice?.deviceTypeId
                        );

                        return {
                          key: addedDevice?.id,
                          deviceTypeId: addedDevice?.deviceTypeId,
                          deviceName: addedDevice?.device?.name,
                          stage: addedDevice?.pmContractWorkPlaceItemStage?.name || '-',
                          total: (addedDevice?.piece ?? 0) - (matchedDevice?.quantity ?? 0),
                        };
                      })}
                      pagination={false}
                      scroll={{ x: 800 }} // Enable horizontal scrolling on small screens
                    />
                  </div>

                  <div style={{ marginTop: 24 }}>
                    <Form.Item name='observations' label='Observații' className='modern-form-item'>
                      <Input.TextArea
                        rows={4}
                        className='modern-textarea'
                        placeholder='Adăugați observații...'
                        onChange={(e: any) => dispatch(updateField({ field: 'observations', value: e.target.value }))}
                      />
                    </Form.Item>

                    <Form.Item name='attachedImage'>
                      <Upload {...getUploadProps()} showUploadList={true} accept='image/png, image/jpeg, image/jpg'>
                        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '20px' }}>
                          <Button icon={<UploadOutlined />}>Atasati Imagine</Button>
                          {!!interventionProjectFile.attachedImage && (
                            <Button type='primary' icon={<DeleteOutlined />} danger onClick={handleDeleteImage}>
                              Stergeti Imagine
                            </Button>
                          )}
                        </div>
                      </Upload>

                      {interventionProjectFile.attachedImage && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '10px',
                            marginTop: '10px',
                            marginLeft: '70px',
                          }}
                        >
                          <Image
                            width={200}
                            src={`data:image/png;base64,${interventionProjectFile.attachedImage}`}
                            alt='Image'
                          />
                        </div>
                      )}
                    </Form.Item>

                    {deviceInstallations.some((d) => d.overInstalled > 0) && (
                      <Alert
                        message='Atenție'
                        description='Există dispozitive cu cantități instalate peste limita contractuală!'
                        type='warning'
                        showIcon
                        className='warning-alert'
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    <Button type='primary' onClick={handleSubmit} size='large' className='submit-button'>
                      Salvează și trimite
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default InterventionFileProject;
